/**
 * WebGL-based asymmetric margin for SDF volumes.
 *
 * Performs morphological dilation/erosion using an ellipsoidal
 * kernel passed as an RGBA32F 3D data texture. The SDF volume
 * uses hardware trilinear interpolation (LINEAR-filtered 3D
 * texture).
 */

import { ReadonlyMat3, ReadonlyVec3, mat3, vec2, vec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { buildEllipsoids } from './AsymmetricMarginConvolution';
import {
    copyTexture3DAsync,
    createSdfTexture3D,
    readTexture3D,
    renderSlicePass,
} from './Texture3DUtils';
import { maybeYield, resetGlDefaults } from './utils';

/**
 * Shader for morphological max/min filter over an ellipsoidal
 * kernel. Both samplers are sampler3D to avoid the WebGL2
 * validation error when different sampler types share the
 * default texture unit at link time.
 */
class AsymmetricMarginShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;

        in vec2 position;
        out vec2 vUV;

        void main() {
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        in vec2 vUV;
        out vec4 fragColor;

        uniform sampler3D uSdfTexture;
        uniform sampler3D uKernelTex;
        uniform int uKernelSize;
        uniform int uKernelWidth;
        uniform float uSliceZ;
        uniform bool uExpand;

        void main() {
            vec3 texCoord = vec3(vUV, uSliceZ);
            float current = texture(uSdfTexture, texCoord).r;

            // No need to loop through neighbors
            if (current <= 0.0 && !uExpand || current >= 1.0 && uExpand) {
                fragColor = vec4(current, current, current, 1.0);
                return;
            }

            float best = current;

            for (int k = 0; k < uKernelSize; k++) {
                int kx = k % uKernelWidth;
                int ky = k / uKernelWidth;
                vec3 offset = texelFetch(uKernelTex, ivec3(kx, ky, 0), 0).rgb;

                vec3 sampleCoord;
                // If expanding, sample neighbors from the direction of the expansion (subtract offset).
                // If contracting, sample from the opposite direction
                if (uExpand) {
                    sampleCoord = texCoord - offset;
                } else {
                    sampleCoord = texCoord + offset;
                }

                float neighbor = texture(uSdfTexture, sampleCoord).r;

                if (uExpand) {
                    if (neighbor > best) {
                        best = neighbor;
                        if (best >= 1.0) { break; }
                    }
                } else {
                    if (neighbor < best) {
                        best = neighbor;
                        if (best <= 0.0) { break; }
                    }
                }
            }

            fragColor = vec4(best, best, best, 1.0);
        }`;

    public readonly uKernelSize: UniformInt;
    public readonly uKernelWidth: UniformInt;
    public readonly uSliceZ: UniformFloat;
    public readonly uExpand: UniformBoolean;

    public constructor() {
        super(
            AsymmetricMarginShader.VERTEX_CONTENT,
            AsymmetricMarginShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uKernelSize = new UniformInt('uKernelSize');
        this.uKernelWidth = new UniformInt('uKernelWidth');
        this.uSliceZ = new UniformFloat('uSliceZ');
        this.uExpand = new UniformBoolean('uExpand');

        super.storeUniformLocations(
            this.uKernelSize,
            this.uKernelWidth,
            this.uSliceZ,
            this.uExpand
        );

        const sdfLoc = gl.getUniformLocation(this.programID, 'uSdfTexture');
        const kernelLoc = gl.getUniformLocation(this.programID, 'uKernelTex');
        this.start();
        gl.uniform1i(sdfLoc, 0);
        gl.uniform1i(kernelLoc, 1);
        this.stop();
    }
}

/**
 * GPU renderer for asymmetric margin operations on SDF
 * volumes. Uses slice-by-slice FBO rendering with ping-pong
 * 3D textures and async readback.
 */
export class AsymmetricMarginRenderer {
    private shader: AsymmetricMarginShader;

    public constructor() {
        this.shader = new AsymmetricMarginShader();
    }

    /**
     * Build an RGBA32F 3D texture (texWidth x texHeight x 1)
     * containing pre-computed normalized texture offsets from
     * LPS kernel offsets. Row width capped at
     * MAX_3D_TEXTURE_SIZE.
     */
    private createKernelTexture(
        lpsKernel: number[][],
        orientation: ReadonlyMat3,
        resX: number,
        resY: number,
        resZ: number,
        nx: number,
        ny: number,
        nz: number
    ): { texture: WebGLTexture; texWidth: number } | null {
        const orientInv = mat3.transpose(mat3.create(), orientation as mat3);
        const kernelSize = lpsKernel.length;
        const maxSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE) as number;
        const texWidth = Math.min(kernelSize, maxSize);
        const texHeight = Math.ceil(kernelSize / texWidth);
        const data = new Float32Array(texWidth * texHeight * 4);
        const tmp = vec3.create();

        for (let k = 0; k < kernelSize; k++) {
            const [dl, dp, ds] = lpsKernel[k];
            vec3.set(tmp, dl, dp, ds);
            vec3.transformMat3(tmp, tmp, orientInv);
            data[k * 4 + 0] = tmp[0] / resX / nx;
            data[k * 4 + 1] = tmp[1] / resY / ny;
            data[k * 4 + 2] = tmp[2] / resZ / nz;
            data[k * 4 + 3] = 0;
        }

        const tex = gl.createTexture();
        if (!tex) {
            return null;
        }

        gl.bindTexture(gl.TEXTURE_3D, tex);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        gl.texImage3D(
            gl.TEXTURE_3D,
            0,
            gl.RGBA32F,
            texWidth,
            texHeight,
            1,
            0,
            gl.RGBA,
            gl.FLOAT,
            data
        );
        gl.bindTexture(gl.TEXTURE_3D, null);

        return { texture: tex, texWidth };
    }

    /**
     * Apply asymmetric margin to a ContourTexture's SDF.
     * Runs expansion and contraction as separate ping-pong
     * passes.
     */
    public async applyMargin(
        src: ContourTexture,
        expansion: number[][],
        contraction: number[][],
        orientation: ReadonlyMat3,
        signal?: AbortSignal
    ): Promise<Uint8Array[]> {
        if (!src.signedDistanceField) {
            throw new Error('AsymmetricMarginGL: source has no SDF');
        }

        const sdf = src.signedDistanceField;
        const nx = sdf.size[0];
        const ny = sdf.size[1];
        const nz = sdf.size[2];

        if (nx === 0 || ny === 0 || nz === 0) {
            return [];
        }

        const grid = src.textureGrid;
        let lastYield = performance.now();

        const tempTex1 = createSdfTexture3D(nx, ny, nz);
        const tempTex2 = createSdfTexture3D(nx, ny, nz);

        if (!tempTex1 || !tempTex2) {
            throw new Error('AsymmetricMarginGL: failed to create textures');
        }

        lastYield = await copyTexture3DAsync(
            sdf.texture,
            tempTex1,
            nx,
            ny,
            nz,
            lastYield,
            signal
        );

        const fbo = new FrameBufferObject(vec2.fromValues(nx, ny));

        let current = tempTex1;
        let other = tempTex2;

        const passes: { lpsKernel: number[][]; expand: boolean }[] = [
            { lpsKernel: expansion, expand: true },
            { lpsKernel: contraction, expand: false },
        ];

        for (const pass of passes) {
            if (pass.lpsKernel.length === 0) {
                continue;
            }

            const kernel = this.createKernelTexture(
                pass.lpsKernel,
                orientation,
                grid.resX,
                grid.resY,
                grid.resZ,
                nx,
                ny,
                nz
            );
            if (!kernel) {
                throw new Error(
                    'AsymmetricMarginGL: ' + 'failed to create kernel texture'
                );
            }

            gl.disable(gl.BLEND);
            this.shader.start();
            gl.viewport(0, 0, nx, ny);
            RenderStorage.getQuadVAO().bind(0);

            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_3D, kernel.texture);

            this.shader.uKernelSize.loadInt(pass.lpsKernel.length);
            this.shader.uKernelWidth.loadInt(kernel.texWidth);
            this.shader.uExpand.loadBoolean(pass.expand);

            renderSlicePass(
                current,
                other,
                fbo,
                this.shader.uSliceZ,
                nz,
                signal
            );
            [current, other] = [other, current];

            RenderStorage.getQuadVAO().unbind(0);
            this.shader.stop();
            fbo.unbind();
            resetGlDefaults();

            gl.deleteTexture(kernel.texture);

            lastYield = await maybeYield(lastYield, signal);
        }

        fbo.delete();

        const resultPixels = readTexture3D(current, nx, ny, nz, signal);

        gl.deleteTexture(tempTex1);
        gl.deleteTexture(tempTex2);

        return resultPixels;
    }

    public delete(): void {
        this.shader.delete();
    }
}

let asymmetricMarginRenderer: AsymmetricMarginRenderer | null = null;

/**
 * Apply GPU-accelerated asymmetric margin to a ContourTexture.
 *
 * @param src          Source ContourTexture with valid SDF
 * @param negMarginMm  Margins for -L, -P, -S directions (mm)
 * @param posMarginMm  Margins for +L, +P, +S directions (mm)
 * @param signal       Optional AbortSignal for cancellation
 * @returns Per-slice SDF (Uint8Array[]) with margin applied
 */
export async function applyAsymmetricMarginGpu(
    src: ContourTexture,
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3,
    signal?: AbortSignal
): Promise<Uint8Array[]> {
    if (!asymmetricMarginRenderer) {
        asymmetricMarginRenderer = new AsymmetricMarginRenderer();
    }

    const grid = src.textureGrid;
    const { expansion, contraction } = buildEllipsoids(
        negMarginMm,
        posMarginMm,
        grid.resX,
        grid.resY,
        grid.resZ
    );

    return asymmetricMarginRenderer.applyMargin(
        src,
        expansion,
        contraction,
        grid.getOrientation(),
        signal
    );
}
